import type { IReferenceWellsFiltersHostBridge, TReferenceWellsFilters } from '@repo/reference-wells';
import { EMPTY_REFERENCE_WELLS_FILTERS } from '@repo/reference-wells';

import { requireService } from 'src/packages/di';
import { FiltersWidgetEntity } from 'src/pages/dashboard/features/filters-widget/FiltersWidget.entity';
import type { FiltersWidgetStore } from 'src/pages/dashboard/features/filters-widget/FiltersWidget.store';
import type { GroupSelectStore } from 'src/pages/dashboard/features/group-select/GroupSelect.store';

function copyAsReferenceWellsFilters(values: TReferenceWellsFilters): TReferenceWellsFilters {
  return {
    status: [...values.status],
    workStartDateFrom: values.workStartDateFrom,
    workStartDateTo: values.workStartDateTo,
    organizationId: [...values.organizationId],
    blockId: [...values.blockId],
    fieldId: [...values.fieldId],
    padId: [...values.padId],
    wellId: [...values.wellId],
    rigModelId: [...values.rigModelId],
    brigadeId: [...values.brigadeId],
    wellboreTypeInstanceId: [...(values.wellboreTypeInstanceId ?? [])],
  };
}

type CreateBridgeOptions = {
  getGroupId: () => string | null | undefined;
  groupSelectStore: GroupSelectStore;
};

/**
 * Thin host bridge: package never imports FiltersWidget / FilterCoordinator.
 * Reads/writes the FiltersWidget in the same widget group as the RW host.
 */
export function createReferenceWellsFiltersHostBridge({
  getGroupId,
  groupSelectStore,
}: CreateBridgeOptions): IReferenceWellsFiltersHostBridge {
  const findFiltersEntity = (): FiltersWidgetEntity | null => {
    const groupId = getGroupId() ?? null;
    const widgets = groupSelectStore.groupManager.getWidgetsByGroupId(groupId);
    const entity = widgets.find((widget) => widget instanceof FiltersWidgetEntity);

    return entity instanceof FiltersWidgetEntity ? entity : null;
  };

  const getFiltersStore = (): FiltersWidgetStore | null => {
    const entity = findFiltersEntity();

    if (!entity) {
      return null;
    }

    return requireService('widgetStoreService').getStore<FiltersWidgetEntity, FiltersWidgetStore>(entity);
  };

  return {
    get hasFiltersWidget(): boolean {
      return findFiltersEntity() !== null;
    },

    pullFilters(): TReferenceWellsFilters {
      const store = getFiltersStore();

      if (store) {
        return copyAsReferenceWellsFilters(store.filtersService.applied);
      }

      const entity = findFiltersEntity();
      const values = entity?.stateParams.values;

      if (!values) {
        return copyAsReferenceWellsFilters(EMPTY_REFERENCE_WELLS_FILTERS);
      }

      return copyAsReferenceWellsFilters({
        ...EMPTY_REFERENCE_WELLS_FILTERS,
        ...values,
        wellboreTypeInstanceId: values.wellboreTypeInstanceId ?? [],
      });
    },

    pushFilters(filters: TReferenceWellsFilters): void {
      const next = { values: copyAsReferenceWellsFilters(filters) };
      const store = getFiltersStore();

      if (store) {
        store.filtersService.hydrate(next);
        store.setStateParams(store.filtersService.getSnapshot());
        return;
      }

      const entity = findFiltersEntity();

      if (entity) {
        entity.setStateParams(next);
      }
    },
  };
}
